"use client";

import { c as _c } from "react/compiler-runtime";
import { AlertTriangle, AlertCircle, Copy, Check, ChevronDown, ChevronUp, Globe, Linkedin, Share2 } from "lucide-react";
import { useMemo, useState } from "react";
import { ModelCodeExampleDialog } from "@/components/models/model-code-example-dialog";
import { ModelCtaButton } from "@/components/models/model-cta-button";
import { ModelStatusBadge } from "@/components/models/model-status-badge";
import { Badge } from "@/lib/components/badge";
import { Button } from "@/lib/components/button";
import { Card } from "@/lib/components/card";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/lib/components/dropdown-menu";
import { TooltipProvider } from "@/lib/components/tooltip";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/lib/components/tooltip";
import { XIcon } from "@/lib/icons/XIcon";
import { formatContextSize, formatDeprecationDate } from "@/lib/utils";
import { cn } from "@/lib/utils";
import { getProviderIcon } from "@llmgateway/shared/components";
import type { ApiModel, ApiModelProviderMapping, ApiProvider } from "@/lib/fetch-models";
import type { StabilityLevel } from "@llmgateway/models";
import type { LucideProps } from "lucide-react";
interface ModelWithProviders extends ApiModel {
  providerDetails: Array<{
    provider: ApiModelProviderMapping;
    providerInfo: ApiProvider;
  }>;
}
function PriceCell(t0) {
  const $ = _c(14);
  const {
    label,
    price,
    discount,
    unit,
    formatPrice,
    multiplier: t1
  } = t0;
  const multiplier = t1 === undefined ? 1 : t1;
  const adjustedPrice = price !== null && price !== undefined && multiplier !== 1 ? String(Number(price) * multiplier) : price;
  let t2;
  if ($[0] !== adjustedPrice || $[1] !== discount || $[2] !== formatPrice) {
    t2 = formatPrice(adjustedPrice, discount);
    $[0] = adjustedPrice;
    $[1] = discount;
    $[2] = formatPrice;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const formatted = t2;
  let t3;
  if ($[4] !== label) {
    t3 = <div className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-1">{label}</div>;
    $[4] = label;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== formatted) {
    t4 = <div className="font-semibold text-foreground text-sm tabular-nums">{formatted}</div>;
    $[6] = formatted;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== unit) {
    t5 = <div className="text-[10px] text-muted-foreground/50">{unit}</div>;
    $[8] = unit;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t3 || $[11] !== t4 || $[12] !== t5) {
    t6 = <div className="text-center">{t3}{t4}{t5}</div>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}
function StabilityDot(t0) {
  const $ = _c(11);
  const {
    stability
  } = t0;
  const level = stability ?? "stable";
  const colorClass = level === "stable" ? "bg-emerald-500" : level === "beta" ? "bg-amber-500" : "bg-red-500";
  let t1;
  if ($[0] !== colorClass) {
    t1 = cn("inline-block h-2 w-2 rounded-full shrink-0", colorClass);
    $[0] = colorClass;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <TooltipTrigger asChild={true}><span className={t1} /></TooltipTrigger>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== level) {
    t3 = level.charAt(0).toUpperCase();
    $[4] = level;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = t3 + level.slice(1);
  let t5;
  if ($[6] !== t4) {
    t5 = <TooltipContent side="top" className="text-xs">{t4}</TooltipContent>;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t2 || $[9] !== t5) {
    t6 = <Tooltip>{t2}{t5}</Tooltip>;
    $[8] = t2;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
}
function isImageGenModel(model: Pick<ApiModel, "output">): boolean {
  return Array.isArray(model.output) && model.output.includes("image");
}
function hasEstimatedImageCost(mapping: ApiModelProviderMapping): boolean {
  return Boolean(mapping.imageOutputPrice && mapping.imageOutputTokensByResolution);
}
export function ModelCard(t0) {
  const $ = _c(94);
  const {
    model,
    shouldShowStabilityWarning,
    getCapabilityIcons,
    goToModel,
    formatPrice
  } = t0;
  const [copiedModel, setCopiedModel] = useState(null);
  const [showAllProviders, setShowAllProviders] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = text => {
      navigator.clipboard.writeText(text);
      setCopiedModel(text);
      setTimeout(() => setCopiedModel(null), 2000);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const copyToClipboard = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = new Date();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const now = t2;
  let t3;
  if ($[2] !== model.providerDetails) {
    t3 = model.providerDetails.length > 0 && model.providerDetails.every(_temp);
    $[2] = model.providerDetails;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const allHaveDeactivatedAt = t3;
  let t4;
  if ($[4] !== allHaveDeactivatedAt || $[5] !== model.providerDetails) {
    t4 = !allHaveDeactivatedAt && model.providerDetails.length > 0 && model.providerDetails.every(_temp2);
    $[4] = allHaveDeactivatedAt;
    $[5] = model.providerDetails;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const allHaveDeprecatedAt = t4;
  let t5;
  if ($[7] !== allHaveDeactivatedAt || $[8] !== model.providerDetails) {
    t5 = allHaveDeactivatedAt && model.providerDetails.every(t6 => {
      const {
        provider: provider_1
      } = t6;
      return new Date(provider_1.deactivatedAt) <= now;
    });
    $[7] = allHaveDeactivatedAt;
    $[8] = model.providerDetails;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const deactivationAllPast = t5;
  let t6;
  if ($[10] !== allHaveDeprecatedAt || $[11] !== model.providerDetails) {
    t6 = allHaveDeprecatedAt && model.providerDetails.every(t7 => {
      const {
        provider: provider_2
      } = t7;
      return new Date(provider_2.deprecatedAt) <= now;
    });
    $[10] = allHaveDeprecatedAt;
    $[11] = model.providerDetails;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const deprecationAllPast = t6;
  const hasProviderStabilityWarning = _temp3;
  let t7;
  if ($[13] !== model.providerDetails) {
    const map = new Map();
    for (const {
      provider: provider_4,
      providerInfo
    } of model.providerDetails) {
      const key = provider_4.providerId;
      if (!map.has(key)) {
        map.set(key, {
          providerInfo,
          providerId: key,
          mappings: []
        });
      }
      map.get(key).mappings.push(provider_4);
    }
    t7 = Array.from(map.values());
    $[13] = model.providerDetails;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const groupedByProvider = t7;
  let max = 0;
  if ($[15] !== max || $[16] !== model.providerDetails) {
    for (const {
      provider: provider_5
    } of model.providerDetails) {
      if (provider_5.discount) {
        const d = parseFloat(provider_5.discount);
        if (d > max) {
          max = d;
        }
      }
    }
    $[15] = max;
    $[16] = model.providerDetails;
    $[17] = max;
  } else {
    max = $[17];
  }
  const bestDiscount = max;
  let t8;
  bb0: {
    if (model.providerDetails.length === 0) {
      let t9;
      if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
        t9 = [];
        $[18] = t9;
      } else {
        t9 = $[18];
      }
      t8 = t9;
      break bb0;
    }
    let result;
    if ($[19] !== getCapabilityIcons || $[20] !== model) {
      const seen = new Set();
      result = [];
      for (const {
        provider: provider_6
      } of model.providerDetails) {
        for (const cap of getCapabilityIcons(provider_6, model)) {
          if (!seen.has(cap.label)) {
            seen.add(cap.label);
            result.push(cap);
          }
        }
      }
      $[19] = getCapabilityIcons;
      $[20] = model;
      $[21] = result;
    } else {
      result = $[21];
    }
    t8 = result;
  }
  const capabilities = t8;
  let t9;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const t10 = model.name ?? model.id;
  let t11;
  if ($[23] !== t10) {
    t11 = <div className="min-w-0 flex-1"><h3 className="text-lg font-bold text-foreground tracking-tight truncate">{t10}</h3></div>;
    $[23] = t10;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== model.stability || $[26] !== shouldShowStabilityWarning) {
    t12 = shouldShowStabilityWarning(model.stability) && <AlertTriangle className="h-4 w-4 text-amber-400" />;
    $[25] = model.stability;
    $[26] = shouldShowStabilityWarning;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== model.id) {
    t13 = <div onClick={_temp4} onMouseDown={_temp5}><ModelCodeExampleDialog modelId={model.id} /></div>;
    $[28] = model.id;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t12 || $[31] !== t13) {
    t14 = <div className="flex items-center gap-1.5 shrink-0">{t12}{t13}</div>;
    $[30] = t12;
    $[31] = t13;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== t11 || $[34] !== t14) {
    t15 = <div className="flex items-start justify-between gap-3 mb-2">{t11}{t14}</div>;
    $[33] = t11;
    $[34] = t14;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== model.family) {
    t16 = <Badge variant="secondary" className="text-[10px] font-medium bg-muted/80 text-muted-foreground border-0 px-2 py-0.5">{model.family}</Badge>;
    $[36] = model.family;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== bestDiscount) {
    t17 = bestDiscount > 0 && <Badge className="text-[10px] px-2 py-0.5 font-semibold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">{Math.round(bestDiscount * 100)}% off</Badge>;
    $[38] = bestDiscount;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== allHaveDeactivatedAt || $[41] !== deactivationAllPast) {
    t18 = allHaveDeactivatedAt && <ModelStatusBadge status="deactivated" isPast={deactivationAllPast} />;
    $[40] = allHaveDeactivatedAt;
    $[41] = deactivationAllPast;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== allHaveDeprecatedAt || $[44] !== deprecationAllPast) {
    t19 = allHaveDeprecatedAt && <ModelStatusBadge status="deprecated" isPast={deprecationAllPast} />;
    $[43] = allHaveDeprecatedAt;
    $[44] = deprecationAllPast;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== t16 || $[47] !== t17 || $[48] !== t18 || $[49] !== t19) {
    t20 = <div className="flex flex-wrap items-center gap-1.5">{t16}{t17}{t18}{t19}</div>;
    $[46] = t16;
    $[47] = t17;
    $[48] = t18;
    $[49] = t19;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] !== t15 || $[52] !== t20) {
    t21 = <div className="mb-4">{t15}{t20}</div>;
    $[51] = t15;
    $[52] = t20;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  let t22;
  if ($[54] !== model.id) {
    t22 = <code className="text-xs font-mono text-muted-foreground flex-1 truncate">{model.id}</code>;
    $[54] = model.id;
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  let t23;
  if ($[56] !== model.id) {
    t23 = e_1 => {
      e_1.stopPropagation();
      copyToClipboard(model.id);
    };
    $[56] = model.id;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  let t24;
  if ($[58] !== copiedModel || $[59] !== model.id) {
    t24 = copiedModel === model.id ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />;
    $[58] = copiedModel;
    $[59] = model.id;
    $[60] = t24;
  } else {
    t24 = $[60];
  }
  let t25;
  if ($[61] !== t23 || $[62] !== t24) {
    t25 = <Button variant="ghost" size="sm" className="h-6 w-6 p-0 shrink-0 text-muted-foreground/60 hover:text-foreground hover:bg-transparent" onClick={t23} title="Copy model ID">{t24}</Button>;
    $[61] = t23;
    $[62] = t24;
    $[63] = t25;
  } else {
    t25 = $[63];
  }
  let t26;
  if ($[64] !== t22 || $[65] !== t25) {
    t26 = <div className="flex items-center gap-1.5 px-2.5 py-2 rounded-md bg-muted/50 border border-border/50 mb-4">{t22}{t25}</div>;
    $[64] = t22;
    $[65] = t25;
    $[66] = t26;
  } else {
    t26 = $[66];
  }
  let t27;
  if ($[67] !== capabilities) {
    t27 = capabilities.length > 0 && <div className="flex flex-wrap gap-1.5 mb-4">{capabilities.map(_temp6)}</div>;
    $[67] = capabilities;
    $[68] = t27;
  } else {
    t27 = $[68];
  }
  let t28;
  if ($[69] !== groupedByProvider || $[70] !== showAllProviders) {
    t28 = showAllProviders ? groupedByProvider : groupedByProvider.slice(0, 1);
    $[69] = groupedByProvider;
    $[70] = showAllProviders;
    $[71] = t28;
  } else {
    t28 = $[71];
  }
  let t29;
  if ($[72] !== copiedModel || $[73] !== formatPrice || $[74] !== model || $[75] !== t28) {
    t29 = t28.map(t30 => {
      const {
        providerInfo: providerInfo_0,
        providerId,
        mappings
      } = t30;
      const ProviderIcon = getProviderIcon(providerId);
      const hasRegions = mappings.length > 1 || mappings.length === 1 && !!mappings[0].region;
      return <ProviderSection key={providerId} modelId={model.id} providerInfo={providerInfo_0} providerId={providerId} ProviderIcon={ProviderIcon} mappings={mappings} hasRegions={hasRegions} hasProviderStabilityWarning={hasProviderStabilityWarning} formatPrice={formatPrice} copyToClipboard={copyToClipboard} copiedModel={copiedModel} isImageGen={isImageGenModel(model)} />;
    });
    $[72] = copiedModel;
    $[73] = formatPrice;
    $[74] = model;
    $[75] = t28;
    $[76] = t29;
  } else {
    t29 = $[76];
  }
  let t30;
  if ($[77] !== groupedByProvider.length || $[78] !== showAllProviders) {
    t30 = groupedByProvider.length > 1 && <button type="button" className="w-full flex items-center justify-center gap-1.5 py-2 text-xs text-muted-foreground hover:text-foreground transition-colors" onClick={e_2 => {
      e_2.stopPropagation();
      setShowAllProviders(_temp7);
    }}>{showAllProviders ? <><ChevronUp className="h-3.5 w-3.5" />Fewer providers</> : <><ChevronDown className="h-3.5 w-3.5" />{groupedByProvider.length - 1} more provider{groupedByProvider.length - 1 > 1 ? "s" : ""}</>}</button>;
    $[77] = groupedByProvider.length;
    $[78] = showAllProviders;
    $[79] = t30;
  } else {
    t30 = $[79];
  }
  let t31;
  if ($[80] !== t29 || $[81] !== t30) {
    t31 = <div className="space-y-3">{t29}{t30}</div>;
    $[80] = t29;
    $[81] = t30;
    $[82] = t31;
  } else {
    t31 = $[82];
  }
  const t32 = `${groupedByProvider[0]?.providerId}/${model.id}`;
  let t33;
  if ($[83] !== t32) {
    t33 = <div className="mt-4 pt-4 border-t border-border/30"><ModelCtaButton modelId={t32} onClick={_temp8} /></div>;
    $[83] = t32;
    $[84] = t33;
  } else {
    t33 = $[84];
  }
  let t34;
  if ($[85] !== t21 || $[86] !== t26 || $[87] !== t27 || $[88] !== t31 || $[89] !== t33) {
    t34 = <div className="p-5">{t21}{t26}{t27}{t31}{t33}</div>;
    $[85] = t21;
    $[86] = t26;
    $[87] = t27;
    $[88] = t31;
    $[89] = t33;
    $[90] = t34;
  } else {
    t34 = $[90];
  }
  let t35;
  if ($[91] !== goToModel || $[92] !== t34) {
    t35 = <TooltipProvider><Card className="group relative overflow-hidden border border-border/50 bg-background hover:border-border transition-all duration-200 cursor-pointer py-0" onClick={goToModel}>{t9}{t34}</Card></TooltipProvider>;
    $[91] = goToModel;
    $[92] = t34;
    $[93] = t35;
  } else {
    t35 = $[93];
  }
  return t35;
}
function _temp8(e_3) {
  return e_3.stopPropagation();
}
function _temp7(v) {
  return !v;
}
function _temp6(t0) {
  const {
    icon: Icon,
    label,
    color
  } = t0;
  return <Tooltip key={label}><TooltipTrigger asChild={true}><div className={cn("inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium cursor-help transition-colors", "bg-muted/50 text-muted-foreground hover:bg-muted border border-transparent hover:border-border/50")}><Icon size={12} className={color} />{label}</div></TooltipTrigger><TooltipContent side="top" className="text-xs">Supports {label.toLowerCase()}</TooltipContent></Tooltip>;
}
function _temp5(e_0) {
  return e_0.stopPropagation();
}
function _temp4(e) {
  return e.stopPropagation();
}
function _temp3(provider_3) {
  return provider_3.stability !== null && provider_3.stability !== undefined && ["unstable", "experimental"].includes(provider_3.stability);
}
function _temp2(t0) {
  const {
    provider: provider_0
  } = t0;
  return provider_0.deprecatedAt;
}
function _temp(t0) {
  const {
    provider
  } = t0;
  return provider.deactivatedAt;
}
function ShareDropdown(t0) {
  const $ = _c(19);
  const {
    modelId,
    providerId
  } = t0;
  const [urlCopied, setUrlCopied] = useState(false);
  const shareUrl = `https://llm-gateway.agenzo.com/models/${encodeURIComponent(modelId)}/${encodeURIComponent(providerId)}`;
  const shareTitle = `${providerId} - ${modelId} on LLM Gateway`;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <DropdownMenuTrigger asChild={true}><Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-muted-foreground/60 hover:text-foreground hover:bg-transparent" onClick={_temp9} title="Share"><Share2 className="h-3 w-3" /></Button></DropdownMenuTrigger>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== shareUrl) {
    t2 = async e_0 => {
      e_0.stopPropagation();
      await navigator.clipboard.writeText(shareUrl);
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    };
    $[1] = shareUrl;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== urlCopied) {
    t3 = urlCopied ? <Check className="h-4 w-4 mr-2 text-green-500" /> : <Copy className="h-4 w-4 mr-2" />;
    $[3] = urlCopied;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = urlCopied ? "Copied!" : "Copy URL";
  let t5;
  if ($[5] !== t2 || $[6] !== t3 || $[7] !== t4) {
    t5 = <DropdownMenuItem onClick={t2} className="cursor-pointer">{t3}{t4}</DropdownMenuItem>;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = `https://x.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`;
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <XIcon className="h-4 w-4 mr-2" />;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t6) {
    t8 = <DropdownMenuItem asChild={true} className="cursor-pointer"><a href={t6} target="_blank" rel="noopener noreferrer" onClick={_temp10}>{t7}Share on X</a></DropdownMenuItem>;
    $[10] = t6;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const t9 = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Linkedin className="h-4 w-4 mr-2" />;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== t9) {
    t11 = <DropdownMenuItem asChild={true} className="cursor-pointer"><a href={t9} target="_blank" rel="noopener noreferrer" onClick={_temp11}>{t10}Share on LinkedIn</a></DropdownMenuItem>;
    $[13] = t9;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== t11 || $[16] !== t5 || $[17] !== t8) {
    t12 = <DropdownMenu>{t1}<DropdownMenuContent align="end">{t5}{t8}{t11}</DropdownMenuContent></DropdownMenu>;
    $[15] = t11;
    $[16] = t5;
    $[17] = t8;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  return t12;
}
function _temp11(e_2) {
  return e_2.stopPropagation();
}
function _temp10(e_1) {
  return e_1.stopPropagation();
}
function _temp9(e) {
  return e.stopPropagation();
}
export function ProviderSection(t0) {
  const $ = _c(136);
  const {
    modelId,
    providerInfo,
    providerId,
    ProviderIcon,
    mappings,
    hasRegions,
    hasProviderStabilityWarning,
    formatPrice,
    copyToClipboard,
    copiedModel,
    isImageGen: t1
  } = t0;
  const isImageGen = t1 === undefined ? false : t1;
  const [activeRegionIdx, setActiveRegionIdx] = useState(0);
  const [showTokenPricing, setShowTokenPricing] = useState(false);
  const [selectedServiceTierId, setSelectedServiceTierId] = useState("standard");
  const activeMapping = mappings[activeRegionIdx] ?? mappings[0];
  let t2;
  if ($[0] !== activeMapping.serviceTiers) {
    t2 = activeMapping.serviceTiers ?? [];
    $[0] = activeMapping.serviceTiers;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t2) {
    t3 = new Set(t2);
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const supportedServiceTierIds = t3;
  let hasImageCostEstimate;
  let providerModelId;
  let serviceTierMultiplier;
  let shouldShowTokenPricing;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[4] !== ProviderIcon || $[5] !== activeMapping || $[6] !== hasProviderStabilityWarning || $[7] !== isImageGen || $[8] !== modelId || $[9] !== providerId || $[10] !== providerInfo.name || $[11] !== providerInfo.serviceTiers || $[12] !== selectedServiceTierId || $[13] !== showTokenPricing || $[14] !== supportedServiceTierIds) {
    let t9;
    if ($[24] !== supportedServiceTierIds) {
      t9 = tier => supportedServiceTierIds.has(tier.id);
      $[24] = supportedServiceTierIds;
      $[25] = t9;
    } else {
      t9 = $[25];
    }
    const serviceTiers = (providerInfo.serviceTiers ?? []).filter(t9);
    const activeServiceTierId = selectedServiceTierId === "standard" || serviceTiers.some(tier_0 => tier_0.id === selectedServiceTierId) ? selectedServiceTierId : "standard";
    serviceTierMultiplier = serviceTiers.find(tier_1 => tier_1.id === activeServiceTierId)?.multiplier ?? 1;
    providerModelId = activeMapping.region ? `${providerId}/${modelId}:${activeMapping.region}` : `${providerId}/${modelId}`;
    let t10;
    if ($[26] !== activeMapping) {
      t10 = hasEstimatedImageCost(activeMapping);
      $[26] = activeMapping;
      $[27] = t10;
    } else {
      t10 = $[27];
    }
    hasImageCostEstimate = t10;
    shouldShowTokenPricing = !isImageGen || showTokenPricing || !hasImageCostEstimate;
    t8 = "flex flex-1 flex-col rounded-lg border border-border/50 bg-muted/20 overflow-hidden";
    t6 = "flex items-center justify-between gap-2 px-3 py-2.5 border-b border-border/30";
    let t11;
    if ($[28] !== ProviderIcon || $[29] !== providerId || $[30] !== providerInfo.name) {
      t11 = <div className="w-5 h-5 rounded flex items-center justify-center shrink-0">{ProviderIcon ? <ProviderIcon className="h-4 w-4" /> : <span className="text-[10px] font-bold text-muted-foreground">{(providerInfo?.name ?? providerId).charAt(0).toUpperCase()}</span>}</div>;
      $[28] = ProviderIcon;
      $[29] = providerId;
      $[30] = providerInfo.name;
      $[31] = t11;
    } else {
      t11 = $[31];
    }
    const t12 = providerInfo?.name ?? providerId;
    let t13;
    if ($[32] !== t12) {
      t13 = <span className="text-sm font-semibold text-foreground truncate">{t12}</span>;
      $[32] = t12;
      $[33] = t13;
    } else {
      t13 = $[33];
    }
    let t14;
    if ($[34] !== activeMapping.stability) {
      t14 = <StabilityDot stability={activeMapping.stability} />;
      $[34] = activeMapping.stability;
      $[35] = t14;
    } else {
      t14 = $[35];
    }
    let t15;
    if ($[36] !== activeMapping || $[37] !== hasProviderStabilityWarning) {
      t15 = hasProviderStabilityWarning(activeMapping) && <AlertTriangle className="h-3.5 w-3.5 text-amber-400 shrink-0" />;
      $[36] = activeMapping;
      $[37] = hasProviderStabilityWarning;
      $[38] = t15;
    } else {
      t15 = $[38];
    }
    if ($[39] !== t11 || $[40] !== t13 || $[41] !== t14 || $[42] !== t15) {
      t7 = <div className="flex items-center gap-2 min-w-0">{t11}{t13}{t14}{t15}</div>;
      $[39] = t11;
      $[40] = t13;
      $[41] = t14;
      $[42] = t15;
      $[43] = t7;
    } else {
      t7 = $[43];
    }
    t4 = "flex items-center gap-1 shrink-0";
    t5 = serviceTiers.length > 0 && <div className="flex h-6 items-center rounded-md border border-border/50 bg-background/80 p-0.5" aria-label="Service tier"><button type="button" onClick={() => setSelectedServiceTierId("standard")} className={cn("inline-flex h-5 items-center gap-1 rounded px-1.5 text-[10px] font-medium leading-none transition-colors", activeServiceTierId === "standard" ? "bg-muted text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground")} aria-pressed={activeServiceTierId === "standard"} title="Standard pricing (1x)"><span>Std</span><span className={cn("text-[9px] font-medium", activeServiceTierId === "standard" ? "text-foreground/65" : "text-muted-foreground/70")}>1x</span></button>{serviceTiers.map(tier_2 => {
        const isSelected = activeServiceTierId === tier_2.id;
        return <button key={tier_2.id} type="button" onClick={() => setSelectedServiceTierId(tier_2.id)} className={cn("inline-flex h-5 items-center gap-1 rounded px-1.5 text-[10px] font-medium leading-none transition-colors", isSelected ? "bg-muted text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground")} aria-pressed={isSelected} title={`${tier_2.name} (${tier_2.multiplier}x pricing)`}><span>{tier_2.name}</span><span className={cn("text-[9px] font-medium", isSelected ? "text-foreground/65" : "text-muted-foreground/70")}>{tier_2.multiplier}x</span></button>;
      })}</div>;
    $[4] = ProviderIcon;
    $[5] = activeMapping;
    $[6] = hasProviderStabilityWarning;
    $[7] = isImageGen;
    $[8] = modelId;
    $[9] = providerId;
    $[10] = providerInfo.name;
    $[11] = providerInfo.serviceTiers;
    $[12] = selectedServiceTierId;
    $[13] = showTokenPricing;
    $[14] = supportedServiceTierIds;
    $[15] = hasImageCostEstimate;
    $[16] = providerModelId;
    $[17] = serviceTierMultiplier;
    $[18] = shouldShowTokenPricing;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
  } else {
    hasImageCostEstimate = $[15];
    providerModelId = $[16];
    serviceTierMultiplier = $[17];
    shouldShowTokenPricing = $[18];
    t4 = $[19];
    t5 = $[20];
    t6 = $[21];
    t7 = $[22];
    t8 = $[23];
  }
  let t9;
  if ($[44] !== modelId || $[45] !== providerId) {
    t9 = <ShareDropdown modelId={modelId} providerId={providerId} />;
    $[44] = modelId;
    $[45] = providerId;
    $[46] = t9;
  } else {
    t9 = $[46];
  }
  let t10;
  if ($[47] !== copyToClipboard || $[48] !== providerModelId) {
    t10 = e => {
      e.stopPropagation();
      copyToClipboard(providerModelId);
    };
    $[47] = copyToClipboard;
    $[48] = providerModelId;
    $[49] = t10;
  } else {
    t10 = $[49];
  }
  let t11;
  if ($[50] !== copiedModel || $[51] !== providerModelId) {
    t11 = copiedModel === providerModelId ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />;
    $[50] = copiedModel;
    $[51] = providerModelId;
    $[52] = t11;
  } else {
    t11 = $[52];
  }
  let t12;
  if ($[53] !== t10 || $[54] !== t11) {
    t12 = <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-muted-foreground/60 hover:text-foreground hover:bg-transparent" onClick={t10} title="Copy provider/model ID">{t11}</Button>;
    $[53] = t10;
    $[54] = t11;
    $[55] = t12;
  } else {
    t12 = $[55];
  }
  let t13;
  if ($[56] !== providerModelId) {
    t13 = <div onClick={_temp12} onMouseDown={_temp13}><ModelCodeExampleDialog modelId={providerModelId} /></div>;
    $[56] = providerModelId;
    $[57] = t13;
  } else {
    t13 = $[57];
  }
  let t14;
  if ($[58] !== t12 || $[59] !== t13 || $[60] !== t4 || $[61] !== t5 || $[62] !== t9) {
    t14 = <div className={t4}>{t5}{t9}{t12}{t13}</div>;
    $[58] = t12;
    $[59] = t13;
    $[60] = t4;
    $[61] = t5;
    $[62] = t9;
    $[63] = t14;
  } else {
    t14 = $[63];
  }
  let t15;
  if ($[64] !== t14 || $[65] !== t6 || $[66] !== t7) {
    t15 = <div className={t6}>{t7}{t14}</div>;
    $[64] = t14;
    $[65] = t6;
    $[66] = t7;
    $[67] = t15;
  } else {
    t15 = $[67];
  }
  let t16;
  if ($[68] !== activeRegionIdx || $[69] !== hasRegions || $[70] !== mappings) {
    t16 = hasRegions && mappings.length > 1 && <div className="flex items-center gap-0.5 px-3 py-1.5 border-b border-border/30 bg-muted/30 overflow-x-auto"><Globe className="h-3 w-3 text-muted-foreground/50 shrink-0 mr-1" />{mappings.map((mapping, idx) => <button key={`${mapping.providerId}-${mapping.region ?? "default"}-${idx}`} type="button" onClick={e_2 => {
        e_2.stopPropagation();
        setActiveRegionIdx(idx);
      }} className={cn("px-2 py-1 rounded text-[10px] font-medium transition-colors whitespace-nowrap", activeRegionIdx === idx ? "bg-background text-foreground shadow-sm border border-border/50" : "text-muted-foreground hover:text-foreground")}>{mapping.region ?? "Default"}</button>)}</div>;
    $[68] = activeRegionIdx;
    $[69] = hasRegions;
    $[70] = mappings;
    $[71] = t16;
  } else {
    t16 = $[71];
  }
  let t17;
  if ($[72] !== activeMapping.contextSize) {
    t17 = activeMapping.contextSize ? formatContextSize(activeMapping.contextSize) : "\u2014";
    $[72] = activeMapping.contextSize;
    $[73] = t17;
  } else {
    t17 = $[73];
  }
  let t18;
  if ($[74] !== t17) {
    t18 = <span className="text-muted-foreground">Context:{" "}<span className="text-foreground font-medium">{t17}</span></span>;
    $[74] = t17;
    $[75] = t18;
  } else {
    t18 = $[75];
  }
  let t19;
  if ($[76] !== activeMapping.discount) {
    t19 = activeMapping.discount && parseFloat(activeMapping.discount) > 0 && <Badge className="text-[10px] px-1.5 py-0 h-4 font-semibold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">{Math.round(parseFloat(activeMapping.discount) * 100)}% off</Badge>;
    $[76] = activeMapping.discount;
    $[77] = t19;
  } else {
    t19 = $[77];
  }
  let t20;
  if ($[78] !== t18 || $[79] !== t19) {
    t20 = <div className="flex items-center justify-between text-xs">{t18}{t19}</div>;
    $[78] = t18;
    $[79] = t19;
    $[80] = t20;
  } else {
    t20 = $[80];
  }
  let t21;
  if ($[81] !== activeMapping.deactivatedAt || $[82] !== activeMapping.deprecatedAt) {
    t21 = (activeMapping.deprecatedAt ?? activeMapping.deactivatedAt) && <div className="flex flex-wrap gap-1.5">{activeMapping.deprecatedAt && <Badge variant="outline" className="text-[10px] px-2 py-0.5 gap-1 bg-amber-500/5 text-amber-600 dark:text-amber-400 border-amber-500/20"><AlertTriangle className="h-2.5 w-2.5" />{formatDeprecationDate(activeMapping.deprecatedAt, "deprecated")}</Badge>}{activeMapping.deactivatedAt && <Badge variant="outline" className="text-[10px] px-2 py-0.5 gap-1 bg-red-500/5 text-red-600 dark:text-red-400 border-red-500/20"><AlertCircle className="h-2.5 w-2.5" />{formatDeprecationDate(activeMapping.deactivatedAt, "deactivated")}</Badge>}</div>;
    $[81] = activeMapping.deactivatedAt;
    $[82] = activeMapping.deprecatedAt;
    $[83] = t21;
  } else {
    t21 = $[83];
  }
  let t22;
  if ($[84] !== activeMapping.discount || $[85] !== activeMapping.imageOutputPrice || $[86] !== activeMapping.imageOutputTokensByResolution || $[87] !== activeMapping.requestPrice || $[88] !== isImageGen || $[89] !== serviceTierMultiplier) {
    t22 = isImageGen && (() => {
      const discountNum = activeMapping.discount ? parseFloat(activeMapping.discount) : 0;
      const requestPriceNum = activeMapping.requestPrice !== null && activeMapping.requestPrice !== undefined ? parseFloat(activeMapping.requestPrice) : 0;
      let perImage = null;
      let label = "Per image";
      let outputCost = 0;
      let resolutionKey = null;
      if (activeMapping.imageOutputPrice && activeMapping.imageOutputTokensByResolution) {
        const outPrice = parseFloat(activeMapping.imageOutputPrice);
        const entries = Object.entries(activeMapping.imageOutputTokensByResolution);
        const preferred = entries.find(_temp14) ?? entries[0];
        if (preferred) {
          outputCost = preferred[1] * outPrice;
          resolutionKey = preferred[0];
        }
        if (requestPriceNum > 0 || outputCost > 0) {
          perImage = (requestPriceNum + outputCost) * serviceTierMultiplier;
          if (resolutionKey) {
            label = `Per image (${resolutionKey})`;
          }
        }
      }
      if (perImage === null) {
        return null;
      }
      const discounted = perImage * (1 - discountNum);
      return <div className="rounded-md bg-muted/40 border border-border/30 p-3"><div className="flex items-center justify-between gap-2"><div className="text-[10px] uppercase tracking-wider text-muted-foreground/70">{label}</div><div className="font-semibold tabular-nums text-sm">{discountNum > 0 ? <><span className="line-through text-muted-foreground mr-1 text-xs">${perImage.toFixed(4)}</span><span className="text-green-600">${discounted.toFixed(4)}</span></> : `$${perImage.toFixed(4)}`}</div></div></div>;
    })();
    $[84] = activeMapping.discount;
    $[85] = activeMapping.imageOutputPrice;
    $[86] = activeMapping.imageOutputTokensByResolution;
    $[87] = activeMapping.requestPrice;
    $[88] = isImageGen;
    $[89] = serviceTierMultiplier;
    $[90] = t22;
  } else {
    t22 = $[90];
  }
  let t23;
  if ($[91] !== activeMapping.cachedInputPrice || $[92] !== activeMapping.discount || $[93] !== activeMapping.inputPrice || $[94] !== activeMapping.outputPrice || $[95] !== activeMapping.perSecondPrice || $[96] !== formatPrice || $[97] !== serviceTierMultiplier || $[98] !== shouldShowTokenPricing) {
    t23 = !shouldShowTokenPricing ? null : activeMapping.perSecondPrice && Object.keys(activeMapping.perSecondPrice).length > 0 ? <div className="rounded-md bg-muted/40 border border-border/30 p-2.5"><div className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-2">Per Second Pricing</div><div className="space-y-1">{(() => {
          const prices = activeMapping.perSecondPrice;
          const defaultVideo = prices.default_video;
          const defaultAudio = prices.default_audio;
          if (defaultVideo && defaultAudio) {
            return <div className="flex justify-between text-sm"><span className="text-muted-foreground">Video / Audio</span><span className="font-semibold tabular-nums">${defaultVideo} – ${defaultAudio}<span className="text-muted-foreground/60 text-xs ml-0.5">/sec</span></span></div>;
          }
          const defaultPrice = prices["default"];
          if (defaultPrice) {
            return <div className="flex justify-between text-sm"><span className="text-muted-foreground">Default</span><span className="font-semibold tabular-nums">${defaultPrice}<span className="text-muted-foreground/60 text-xs ml-0.5">/sec</span></span></div>;
          }
          return Object.entries(prices).map(_temp15);
        })()}</div></div> : <div className="space-y-2"><div className="grid grid-cols-3 gap-px rounded-md bg-border/30 border border-border/30 overflow-hidden"><div className="bg-background p-2"><PriceCell label="Input" price={activeMapping.inputPrice} discount={activeMapping.discount} unit="/M tokens" formatPrice={formatPrice} multiplier={serviceTierMultiplier} /></div><div className="bg-background p-2"><PriceCell label="Cached" price={activeMapping.cachedInputPrice} discount={activeMapping.discount} unit="/M tokens" formatPrice={formatPrice} multiplier={serviceTierMultiplier} /></div><div className="bg-background p-2"><PriceCell label="Output" price={activeMapping.outputPrice} discount={activeMapping.discount} unit="/M tokens" formatPrice={formatPrice} multiplier={serviceTierMultiplier} /></div></div></div>;
    $[91] = activeMapping.cachedInputPrice;
    $[92] = activeMapping.discount;
    $[93] = activeMapping.inputPrice;
    $[94] = activeMapping.outputPrice;
    $[95] = activeMapping.perSecondPrice;
    $[96] = formatPrice;
    $[97] = serviceTierMultiplier;
    $[98] = shouldShowTokenPricing;
    $[99] = t23;
  } else {
    t23 = $[99];
  }
  let t24;
  if ($[100] !== activeMapping.discount || $[101] !== activeMapping.pricingTiers || $[102] !== formatPrice || $[103] !== serviceTierMultiplier) {
    t24 = (activeMapping.pricingTiers?.length ?? 0) > 1 && <div className="rounded-md bg-muted/40 border border-border/30 p-2.5"><div className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-2">Tiered Pricing</div><div className="space-y-2">{(() => {
          const hasCached = activeMapping.pricingTiers.some(_temp16);
          return <><div className={`grid ${hasCached ? "grid-cols-[1fr_1fr_1fr_1fr]" : "grid-cols-[1fr_1fr_1fr]"} gap-x-2 text-[10px] text-muted-foreground/60 text-right`}><div /><div>IN</div>{hasCached && <div>CACHED</div>}<div>OUT</div></div>{activeMapping.pricingTiers.map((tier_3, index) => {
              const discountNum_0 = activeMapping.discount ? parseFloat(activeMapping.discount) : 0;
              const prevTokens = activeMapping.pricingTiers[index - 1]?.upToTokens ?? 0;
              const label_0 = tier_3.upToTokens === null ? `>${(prevTokens / 1000).toLocaleString()}K tokens` : `≤${(tier_3.upToTokens / 1000).toLocaleString()}K tokens`;
              return <div key={index} className={`grid ${hasCached ? "grid-cols-[1fr_1fr_1fr_1fr]" : "grid-cols-[1fr_1fr_1fr]"} gap-x-2 items-center text-xs`}><span className="text-muted-foreground">{label_0}</span><div className="font-mono tabular-nums flex justify-end">{formatPrice(tier_3.inputPrice, discountNum_0 > 0 ? String(discountNum_0) : null, "end", serviceTierMultiplier)}</div>{hasCached && <div className="font-mono tabular-nums flex justify-end">{tier_3.cachedInputPrice ? formatPrice(tier_3.cachedInputPrice, discountNum_0 > 0 ? String(discountNum_0) : null, "end", serviceTierMultiplier) : "\u2014"}</div>}<div className="font-mono tabular-nums flex justify-end">{formatPrice(tier_3.outputPrice, discountNum_0 > 0 ? String(discountNum_0) : null, "end", serviceTierMultiplier)}</div></div>;
            })}</>;
        })()}</div></div>;
    $[100] = activeMapping.discount;
    $[101] = activeMapping.pricingTiers;
    $[102] = formatPrice;
    $[103] = serviceTierMultiplier;
    $[104] = t24;
  } else {
    t24 = $[104];
  }
  let t25;
  if ($[105] !== activeMapping.discount || $[106] !== activeMapping.imageInputPrice || $[107] !== activeMapping.imageInputTokensByResolution || $[108] !== activeMapping.imageOutputPrice || $[109] !== activeMapping.imageOutputTokensByResolution || $[110] !== formatPrice || $[111] !== serviceTierMultiplier) {
    t25 = (activeMapping.imageInputTokensByResolution ?? activeMapping.imageOutputTokensByResolution ?? activeMapping.imageOutputPrice) && <div className="rounded-md bg-muted/40 border border-border/30 p-2.5"><div className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-2">{activeMapping.imageOutputTokensByResolution ? "Image Pricing (est. per image)" : "Image Pricing"}</div>{activeMapping.imageInputPrice && activeMapping.imageInputTokensByResolution && (() => {
        const imageInputPriceNum = parseFloat(activeMapping.imageInputPrice);
        const named = Object.entries(activeMapping.imageInputTokensByResolution).filter(_temp17);
        const defaultTokens = activeMapping.imageInputTokensByResolution["default"];
        const entries_0 = named.length > 0 ? named : defaultTokens !== undefined ? [["any size", defaultTokens]] : [];
        if (entries_0.length === 0) {
          return null;
        }
        const discountNum_1 = activeMapping.discount ? parseFloat(activeMapping.discount) : 0;
        return <div className="mb-1.5"><div className="text-[10px] text-muted-foreground mb-0.5">Input</div>{entries_0.map(t26 => {
            const [res, tokensPerImage] = t26;
            const raw = tokensPerImage * imageInputPriceNum * serviceTierMultiplier;
            const discounted_0 = raw * (1 - discountNum_1);
            return <div key={res} className="flex justify-between items-center text-xs py-0.5"><span className="text-muted-foreground">{res}</span><span className="font-mono tabular-nums">{discountNum_1 > 0 ? <><span className="line-through text-muted-foreground mr-1">~${raw.toFixed(4)}</span><span className="text-green-600 font-semibold">~${discounted_0.toFixed(4)}</span></> : `~$${raw.toFixed(4)}`}</span></div>;
          })}</div>;
      })()}{activeMapping.imageOutputPrice && !activeMapping.imageOutputTokensByResolution && <div><div className="text-[10px] text-muted-foreground mb-0.5">Image Output</div><div className="flex justify-between items-center text-xs py-0.5"><span className="text-muted-foreground">Tokens</span><div className="font-mono tabular-nums flex items-center gap-1">{formatPrice(activeMapping.imageOutputPrice, activeMapping.discount, "end", serviceTierMultiplier)}<span className="text-muted-foreground/60">/M tokens</span></div></div></div>}{activeMapping.imageOutputPrice && activeMapping.imageOutputTokensByResolution && (() => {
        const imageOutputPriceNum = parseFloat(activeMapping.imageOutputPrice);
        const entries_1 = Object.entries(activeMapping.imageOutputTokensByResolution).filter(_temp18);
        if (entries_1.length === 0) {
          return null;
        }
        const discountNum_2 = activeMapping.discount ? parseFloat(activeMapping.discount) : 0;
        return <div><div className="text-[10px] text-muted-foreground mb-0.5">Output</div>{entries_1.map(t27 => {
            const [res_0, tokensPerImage_0] = t27;
            const raw_0 = tokensPerImage_0 * imageOutputPriceNum * serviceTierMultiplier;
            const discounted_1 = raw_0 * (1 - discountNum_2);
            return <div key={res_0} className="flex justify-between items-center text-xs py-0.5"><span className="text-muted-foreground">{res_0}</span><span className="font-mono tabular-nums">{discountNum_2 > 0 ? <><span className="line-through text-muted-foreground mr-1">~${raw_0.toFixed(4)}</span><span className="text-green-600 font-semibold">~${discounted_1.toFixed(4)}</span></> : `~$${raw_0.toFixed(4)}`}</span></div>;
          })}</div>;
      })()}</div>;
    $[105] = activeMapping.discount;
    $[106] = activeMapping.imageInputPrice;
    $[107] = activeMapping.imageInputTokensByResolution;
    $[108] = activeMapping.imageOutputPrice;
    $[109] = activeMapping.imageOutputTokensByResolution;
    $[110] = formatPrice;
    $[111] = serviceTierMultiplier;
    $[112] = t25;
  } else {
    t25 = $[112];
  }
  let t26;
  if ($[113] !== activeMapping.discount || $[114] !== activeMapping.requestPrice || $[115] !== activeMapping.webSearchPrice || $[116] !== isImageGen) {
    t26 = !isImageGen && activeMapping.requestPrice !== null && activeMapping.requestPrice !== undefined && parseFloat(activeMapping.requestPrice) > 0 || activeMapping.webSearchPrice !== null && activeMapping.webSearchPrice !== undefined && parseFloat(activeMapping.webSearchPrice) > 0 ? <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">{!isImageGen && activeMapping.requestPrice !== null && activeMapping.requestPrice !== undefined && parseFloat(activeMapping.requestPrice) > 0 && (() => {
        const original = parseFloat(activeMapping.requestPrice);
        const discountNum_3 = activeMapping.discount ? parseFloat(activeMapping.discount) : 0;
        return <span>+{" "}{discountNum_3 > 0 ? <><span className="line-through text-muted-foreground/60 mr-1">${original.toFixed(3)}</span><span className="text-green-600 font-semibold">${(original * (1 - discountNum_3)).toFixed(3)}</span></> : `$${original.toFixed(3)}`}<span className="text-muted-foreground/60">{" "}per request</span></span>;
      })()}{activeMapping.webSearchPrice !== null && activeMapping.webSearchPrice !== undefined && parseFloat(activeMapping.webSearchPrice) > 0 && (() => {
        const original_0 = parseFloat(activeMapping.webSearchPrice);
        const discountNum_4 = activeMapping.discount ? parseFloat(activeMapping.discount) : 0;
        return <span>+{" "}{discountNum_4 > 0 ? <><span className="line-through text-muted-foreground/60 mr-1">${original_0.toFixed(3)}</span><span className="text-green-600 font-semibold">${(original_0 * (1 - discountNum_4)).toFixed(3)}</span></> : `$${original_0.toFixed(3)}`}<span className="text-muted-foreground/60">{" "}per search</span></span>;
      })()}</div> : null;
    $[113] = activeMapping.discount;
    $[114] = activeMapping.requestPrice;
    $[115] = activeMapping.webSearchPrice;
    $[116] = isImageGen;
    $[117] = t26;
  } else {
    t26 = $[117];
  }
  let t27;
  if ($[118] !== hasImageCostEstimate || $[119] !== isImageGen || $[120] !== showTokenPricing) {
    t27 = isImageGen && hasImageCostEstimate && <button type="button" className="w-full flex items-center justify-center gap-1.5 py-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors" onClick={e_3 => {
      e_3.stopPropagation();
      setShowTokenPricing(_temp19);
    }}>{showTokenPricing ? <><ChevronUp className="h-3 w-3" />Hide token pricing</> : <><ChevronDown className="h-3 w-3" />Expand details</>}</button>;
    $[118] = hasImageCostEstimate;
    $[119] = isImageGen;
    $[120] = showTokenPricing;
    $[121] = t27;
  } else {
    t27 = $[121];
  }
  let t28;
  if ($[122] !== t20 || $[123] !== t21 || $[124] !== t22 || $[125] !== t23 || $[126] !== t24 || $[127] !== t25 || $[128] !== t26 || $[129] !== t27) {
    t28 = <div className="px-3 py-3 space-y-3">{t20}{t21}{t22}{t23}{t24}{t25}{t26}{t27}</div>;
    $[122] = t20;
    $[123] = t21;
    $[124] = t22;
    $[125] = t23;
    $[126] = t24;
    $[127] = t25;
    $[128] = t26;
    $[129] = t27;
    $[130] = t28;
  } else {
    t28 = $[130];
  }
  let t29;
  if ($[131] !== t15 || $[132] !== t16 || $[133] !== t28 || $[134] !== t8) {
    t29 = <div className={t8}>{t15}{t16}{t28}</div>;
    $[131] = t15;
    $[132] = t16;
    $[133] = t28;
    $[134] = t8;
    $[135] = t29;
  } else {
    t29 = $[135];
  }
  return t29;
}
function _temp19(v) {
  return !v;
}
function _temp18(t0) {
  const [k_1] = t0;
  return k_1 !== "default";
}
function _temp17(t0) {
  const [k_0] = t0;
  return k_0 !== "default";
}
function _temp16(t) {
  return t.cachedInputPrice;
}
function _temp15(t0) {
  const [key, value] = t0;
  return <div key={key} className="flex justify-between text-xs"><span className="text-muted-foreground">{key}</span><span className="font-mono tabular-nums">${value}/sec</span></div>;
}
function _temp14(t0) {
  const [k] = t0;
  return k !== "default";
}
function _temp13(e_1) {
  return e_1.stopPropagation();
}
function _temp12(e_0) {
  return e_0.stopPropagation();
}